import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  TextField,
  Button
} from '@mui/material';
import { Link as LinkIcon } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { changeLanguage } from '../../../redux/actions/languageActions';
import { FlagIcon } from 'react-flag-kit';

// Language options with country codes for flag images
const languageOptions = [
  { code: 'EN', name: 'English', countryCode: 'US' },
  { code: 'AR', name: 'Arabic', countryCode: 'SA' },
  { code: 'BG', name: 'Bulgarian', countryCode: 'BG' },
  { code: 'ZH', name: 'Chinese', countryCode: 'CN' },
  { code: 'HR', name: 'Croatian', countryCode: 'HR' },
  { code: 'CS', name: 'Czech', countryCode: 'CZ' },
  { code: 'DA', name: 'Danish', countryCode: 'DK' },
  { code: 'NL', name: 'Dutch', countryCode: 'NL' },
  { code: 'FIL', name: 'Filipino', countryCode: 'PH' },
  { code: 'FI', name: 'Finnish', countryCode: 'FI' },
  { code: 'FR', name: 'French', countryCode: 'FR' },
  { code: 'DE', name: 'German', countryCode: 'DE' },
  { code: 'EL', name: 'Greek', countryCode: 'GR' },
  { code: 'HI', name: 'Hindi', countryCode: 'IN' },
  { code: 'HU', name: 'Hungarian', countryCode: 'HU' },
  { code: 'ID', name: 'Indonesian', countryCode: 'ID' },
  { code: 'IT', name: 'Italian', countryCode: 'IT' },
  { code: 'JA', name: 'Japanese', countryCode: 'JP' },
  { code: 'KO', name: 'Korean', countryCode: 'KR' },
  { code: 'MS', name: 'Malay', countryCode: 'MY' },
  { code: 'NO', name: 'Norwegian', countryCode: 'NO' },
  { code: 'PL', name: 'Polish', countryCode: 'PL' },
  { code: 'PT', name: 'Portuguese', countryCode: 'PT' },
  { code: 'RO', name: 'Romanian', countryCode: 'RO' },
  { code: 'RU', name: 'Russian', countryCode: 'RU' },
  { code: 'SK', name: 'Slovak', countryCode: 'SK' },
  { code: 'ES', name: 'Spanish', countryCode: 'ES' },
  { code: 'SV', name: 'Swedish', countryCode: 'SE' },
  { code: 'TH', name: 'Thai', countryCode: 'TH' },
  { code: 'TR', name: 'Turkish', countryCode: 'TR' },
  { code: 'UK', name: 'Ukrainian', countryCode: 'UA' },
  { code: 'UR', name: 'Urdu', countryCode: 'PK' },
  { code: 'VI', name: 'Vietnamese', countryCode: 'VN' }
];

// Special options for Additional Languages
const specialOptions = [
  { code: 'all', name: 'All', isSpecial: true },
  { code: 'none', name: 'None', isSpecial: true }
];

// Combined options for Additional Languages dropdown (excluding selected agent language)
const getAllLanguageOptions = (selectedAgentLanguage) => {
  const filteredLanguageOptions = languageOptions.filter(lang => lang.code !== selectedAgentLanguage);
  return [...specialOptions, ...filteredLanguageOptions];
};

function LanguageSettings() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state.language);
  
  const [agentLanguage, setAgentLanguage] = useState(user?.defaultLanguage || 'EN');
  const [additionalLanguages, setAdditionalLanguages] = useState([]);

  // Initialize additional languages from user data
  useEffect(() => {
    if (user?.additionalLanguage && Array.isArray(user.additionalLanguage)) {
      const userAdditionalLanguages = user.additionalLanguage.map(langCode => 
        languageOptions.find(lang => lang.code === langCode)
      ).filter(Boolean);
      setAdditionalLanguages(userAdditionalLanguages);
    }
  }, [user]);

  // Remove agent language from additional languages when agent language changes
  useEffect(() => {
    setAdditionalLanguages(prev => 
      prev.filter(lang => lang.code !== agentLanguage)
    );
  }, [agentLanguage]);

  const handleAgentLanguageChange = (event) => {
    setAgentLanguage(event.target.value);
  };

  const handleAdditionalLanguagesChange = (event, newValue) => {
    // Handle special options
    if (newValue.some(item => item.code === 'all')) {
      // If "All" is selected, select all languages except special options and agent language
      const filteredOptions = languageOptions.filter(lang => lang.code !== agentLanguage);
      setAdditionalLanguages(filteredOptions);
    } else if (newValue.some(item => item.code === 'none')) {
      // If "None" is selected, clear all selections
      setAdditionalLanguages([]);
    } else {
      // Regular selection
      setAdditionalLanguages(newValue);
    }
  };

  const handleSave = async () => {
    try {
      const languageData = {
        defaultLanguage: agentLanguage,
        additionalLanguage: additionalLanguages.map(lang => lang.code)
      };
      
      await dispatch(changeLanguage(languageData));
    } catch (error) {
      console.error('Failed to save language settings:', error);
    }
  };

  const selectedAgentLanguage = languageOptions.find(lang => lang.code === agentLanguage);

  return (
    <Box sx={{ maxWidth: 700 }}>
      {/* Agent Language Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LinkIcon sx={{ mr: 1, color: '#6b7280' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                Agent Language
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Choose the default language the agent will communicate in.
            </Typography>
          </Box>
          
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={agentLanguage}
              onChange={handleAgentLanguageChange}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 400,
                  },
                },
              }}
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 1
                }
              }}
              renderValue={(value) => {
                const selectedLang = languageOptions.find(lang => lang.code === value);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: '1.5rem', 
                      height: '1.5rem', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#f9fafb'
                    }}>
                      <FlagIcon 
                        code={selectedLang?.countryCode} 
                        size={20}
                        style={{ borderRadius: '50%' }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 400 }}>{selectedLang?.name}</Typography>
                  </Box>
                );
              }}
            >
              {languageOptions.map((language) => (
                <MenuItem key={language.code} value={language.code}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                    <Box sx={{ 
                      width: '1.5rem', 
                      height: '1.5rem', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#f9fafb'
                    }}>
                      <FlagIcon 
                        code={language.countryCode} 
                        size={20}
                        style={{ borderRadius: '50%' }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 400 }}>{language.name}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Additional Languages Section */}
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Box >
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LinkIcon sx={{ mr: 1, color: '#6b7280' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                Additional Languages
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Specify additional languages which callers can choose from.
            </Typography>
          </Box>
          
          <Autocomplete
            multiple
            size="small"
            options={getAllLanguageOptions(agentLanguage)}
            value={additionalLanguages}
            onChange={handleAdditionalLanguagesChange}
            getOptionLabel={(option) => option.name}
            disableCloseOnSelect
            renderOption={(props, option) => {
              const isSelected = additionalLanguages.some(lang => lang.code === option.code);
              return (
                <Box component="li" {...props}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    py: 0.5,
                    width: '100%',
                    position: 'relative'
                  }}>
                    {option.isSpecial ? (
                      <Box sx={{ width: '1.5rem', height: '1.5rem' }}></Box>
                    ) : (
                      <Box sx={{ 
                        width: '1.5rem', 
                        height: '1.5rem', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '1px solid #e5e7eb',
                        backgroundColor: '#f9fafb'
                      }}>
                        <FlagIcon 
                          code={option.countryCode} 
                          size={20}
                          style={{ borderRadius: '50%' }}
                        />
                      </Box>
                    )}
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 400, flex: 1 }}>{option.name}</Typography>
                    {isSelected && option.code !== 'none' && (
                      <Box sx={{ 
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)'
                      }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#000' }}>✓</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.code}
                  size="small"
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {option.countryCode && (
                        <Box sx={{ 
                          width: '1rem', 
                          height: '1rem', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          border: '1px solid #e5e7eb',
                          backgroundColor: '#f9fafb'
                        }}>
                          <FlagIcon 
                            code={option.countryCode} 
                            size={12}
                            style={{ borderRadius: '50%' }}
                          />
                        </Box>
                      )}
                      <Typography variant="caption">{option.name}</Typography>
                    </Box>
                  }
                  sx={{
                    '& .MuiChip-label': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Add additional languages"
                sx={{
                  minWidth: 200,
                  '& .MuiOutlinedInput-root': {
                    '& .MuiAutocomplete-input': {
                      minWidth: '120px'
                    }
                  }
                }}
              />
            )}
            ListboxProps={{
              style: {
                maxHeight: 400,
                padding: '8px 0',
              },
            }}
            sx={{
              minWidth: 200
            }}
          />
        </Box>
      </Paper>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          sx={{
            color: 'white',
            px: 3,
            py: 1,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            
          }}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
}

export default LanguageSettings;
